import { ref, reactive, inject, getCurrentInstance } from 'vue';
const CONSENT_VERSION = '1.0';
const cookieManagerInstances = new Map();
/**
 * Cookie Management Composable - Shared Version
 * Provides DSGVO-compliant cookie management functionality
 * Cookie keys are automatically derived from project configuration
 */
export const useCookieManager = (options = {}) => {
    // Try to get project config from injection (only inside component setup)
    const instance = getCurrentInstance();
    const projectConfig = instance
        ? inject('projectConfig', null)
        : null;
    // Determine project identifier from options or config
    const projectIdentifier = options.projectIdentifier ||
        projectConfig?.projectIdentifier ||
        'app';
    const consentVersion = options.consentVersion || CONSENT_VERSION;
    const normalizedIdentifier = projectIdentifier.replace(/\./g, '_');
    const cacheKey = `${normalizedIdentifier}:${consentVersion}`;
    const cachedInstance = cookieManagerInstances.get(cacheKey);
    if (cachedInstance) {
        return cachedInstance;
    }
    // Generate cookie keys based on project identifier
    const COOKIE_CONSENT_KEY = `${normalizedIdentifier}_cookie_consent`;
    const COOKIE_SETTINGS_KEY = `${normalizedIdentifier}_cookie_settings`;
    console.log(`🍪 CookieManager initialized for: ${projectIdentifier}`);
    console.log(`   Consent key: ${COOKIE_CONSENT_KEY}`);
    // Global reactive state
    const cookieSettings = reactive({
        essential: true,
        analytics: false,
        marketing: false,
        externalContent: false,
        functional: false
    });
    const hasConsent = ref(false);
    /**
     * Check if user has given valid consent
     */
    const hasValidConsent = () => {
        try {
            const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
            if (!stored)
                return false;
            const consent = JSON.parse(stored);
            // Check if consent is current version and not older than 1 year
            const oneYearAgo = Date.now() - (365 * 24 * 60 * 60 * 1000);
            return consent.version === consentVersion && consent.timestamp > oneYearAgo;
        }
        catch {
            return false;
        }
    };
    /**
     * Load existing cookie settings from localStorage
     */
    const loadSettings = () => {
        try {
            const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
            if (stored) {
                const consent = JSON.parse(stored);
                const normalizedSettings = {
                    essential: consent.settings?.essential !== false,
                    analytics: Boolean(consent.settings?.analytics),
                    marketing: Boolean(consent.settings?.marketing),
                    // Migration: alte Zustimmungen für "marketing" gelten für externe Inhalte.
                    externalContent: Boolean(consent.settings?.externalContent ?? consent.settings?.marketing),
                    functional: Boolean(consent.settings?.functional)
                };
                consent.settings = normalizedSettings;
                Object.assign(cookieSettings, consent.settings);
                hasConsent.value = true;
            }
        }
        catch {
            // Use default settings if loading fails
            hasConsent.value = false;
        }
    };
    /**
     * Save cookie consent to localStorage
     */
    const saveConsent = (settings = {}) => {
        // Update settings with provided values
        Object.assign(cookieSettings, settings);
        const consent = {
            version: consentVersion,
            timestamp: Date.now(),
            settings: { ...cookieSettings }
        };
        try {
            localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
            localStorage.setItem(COOKIE_SETTINGS_KEY, JSON.stringify(cookieSettings));
            hasConsent.value = true;
        }
        catch (error) {
            console.warn('Could not save cookie consent:', error);
        }
    };
    /**
     * Set a cookie with proper security settings
     */
    const setCookie = (name, value, days = 365) => {
        const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `${name}=${value}; expires=${expires}; path=/; secure; samesite=strict`;
    };
    /**
     * Get a cookie value by name
     */
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift() || null;
        }
        return null;
    };
    /**
     * Delete a cookie by setting its expiry to the past
     */
    const deleteCookie = (name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    };
    /**
     * Check if a specific cookie category is enabled
     */
    const isCategoryEnabled = (category) => {
        return cookieSettings[category];
    };
    /**
     * Initialize analytics tracking (placeholder)
     */
    const initializeAnalytics = () => {
        if (!cookieSettings.analytics)
            return;
        console.log('🔍 Initializing analytics...');
        // Google Analytics 4 example
        if (typeof gtag !== 'undefined') {
            gtag('consent', 'update', {
                analytics_storage: 'granted'
            });
        }
        // Matomo example
        if (typeof _paq !== 'undefined') {
            _paq.push(['rememberConsentGiven']);
        }
        setCookie('analytics_enabled', 'true');
    };
    /**
     * Disable analytics tracking
     */
    const disableAnalytics = () => {
        console.log('🔍 Disabling analytics...');
        // Google Analytics 4 example
        if (typeof gtag !== 'undefined') {
            gtag('consent', 'update', {
                analytics_storage: 'denied'
            });
        }
        // Matomo example
        if (typeof _paq !== 'undefined') {
            _paq.push(['forgetConsentGiven']);
        }
        deleteCookie('analytics_enabled');
        deleteCookie('_ga');
        deleteCookie('_ga_' + 'GA_MEASUREMENT_ID'); // Replace with your GA4 measurement ID
    };
    /**
     * Initialize marketing tracking (placeholder)
     */
    const initializeMarketing = () => {
        if (!cookieSettings.marketing)
            return;
        console.log('📢 Initializing marketing...');
        // Facebook Pixel example
        if (typeof fbq !== 'undefined') {
            fbq('consent', 'grant');
        }
        // Google Ads example
        if (typeof gtag !== 'undefined') {
            gtag('consent', 'update', {
                ad_storage: 'granted',
                ad_user_data: 'granted',
                ad_personalization: 'granted'
            });
        }
        setCookie('marketing_enabled', 'true');
    };
    /**
     * Disable marketing tracking
     */
    const disableMarketing = () => {
        console.log('📢 Disabling marketing...');
        // Facebook Pixel example
        if (typeof fbq !== 'undefined') {
            fbq('consent', 'revoke');
        }
        // Google Ads example
        if (typeof gtag !== 'undefined') {
            gtag('consent', 'update', {
                ad_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied'
            });
        }
        deleteCookie('marketing_enabled');
        deleteCookie('_gcl_au'); // Google Ads
        deleteCookie('fr'); // Facebook
    };
    /**
     * Initialize external content features (e.g. Google reviews embeds/content).
     */
    const initializeExternalContent = () => {
        if (!cookieSettings.externalContent)
            return;
        setCookie('external_content_enabled', 'true');
    };
    /**
     * Disable external content features.
     */
    const disableExternalContent = () => {
        deleteCookie('external_content_enabled');
    };
    /**
     * Initialize functional features
     */
    const initializeFunctional = () => {
        if (!cookieSettings.functional)
            return;
        console.log('⚙️ Initializing functional features...');
        // Chat widgets, preference storage, etc.
        setCookie('functional_enabled', 'true');
        // Example: Initialize chat widget
        // if (typeof window.intercomSettings !== 'undefined') {
        //   window.Intercom('boot', window.intercomSettings)
        // }
    };
    /**
     * Disable functional features
     */
    const disableFunctional = () => {
        console.log('⚙️ Disabling functional features...');
        deleteCookie('functional_enabled');
        // Example: Disable chat widget
        // if (typeof window.Intercom !== 'undefined') {
        //   window.Intercom('shutdown')
        // }
    };
    /**
     * Apply current cookie settings to tracking services
     */
    const applySettings = () => {
        // Essential cookies are always active
        setCookie('essential_enabled', 'true');
        // Apply analytics settings
        if (cookieSettings.analytics) {
            initializeAnalytics();
        }
        else {
            disableAnalytics();
        }
        // Apply marketing settings
        if (cookieSettings.marketing) {
            initializeMarketing();
        }
        else {
            disableMarketing();
        }
        // Apply external content settings
        if (cookieSettings.externalContent) {
            initializeExternalContent();
        }
        else {
            disableExternalContent();
        }
        // Apply functional settings
        if (cookieSettings.functional) {
            initializeFunctional();
        }
        else {
            disableFunctional();
        }
    };
    /**
     * Accept all cookies
     */
    const acceptAll = () => {
        saveConsent({
            essential: true,
            analytics: true,
            marketing: true,
            externalContent: true,
            functional: true
        });
        applySettings();
    };
    /**
     * Accept only essential cookies
     */
    const acceptEssential = () => {
        saveConsent({
            essential: true,
            analytics: false,
            marketing: false,
            externalContent: false,
            functional: false
        });
        applySettings();
    };
    /**
     * Update specific cookie category
     */
    const updateCategory = (category, enabled) => {
        if (category === 'essential')
            return; // Essential cannot be disabled
        cookieSettings[category] = enabled;
        saveConsent();
        applySettings();
    };
    /**
     * Reset all cookie settings to defaults
     */
    const resetSettings = () => {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        localStorage.removeItem(COOKIE_SETTINGS_KEY);
        cookieSettings.essential = true;
        cookieSettings.analytics = false;
        cookieSettings.marketing = false;
        cookieSettings.externalContent = false;
        cookieSettings.functional = false;
        hasConsent.value = false;
        // Clear all tracking cookies
        disableAnalytics();
        disableMarketing();
        disableExternalContent();
        disableFunctional();
    };
    /**
     * Initialize cookie manager
     */
    const initialize = () => {
        loadSettings();
        if (hasValidConsent()) {
            applySettings();
        }
    };
    const instanceApi = {
        // State
        cookieSettings,
        hasConsent,
        // Methods
        hasValidConsent,
        loadSettings,
        saveConsent,
        setCookie,
        getCookie,
        deleteCookie,
        isCategoryEnabled,
        applySettings,
        acceptAll,
        acceptEssential,
        updateCategory,
        resetSettings,
        initialize
    };
    cookieManagerInstances.set(cacheKey, instanceApi);
    return instanceApi;
};
// Global cookie manager instance factory
export const createCookieManager = (options) => {
    return useCookieManager(options);
};
// Export lazy singleton proxy for backward compatibility
export const cookieManager = new Proxy({}, {
    get(_target, prop) {
        const manager = useCookieManager();
        const value = manager[prop];
        return typeof value === 'function' ? value.bind(manager) : value;
    },
    set(_target, prop, value) {
        ;
        useCookieManager()[prop] = value;
        return true;
    }
});
